import { type RecommendationSettings } from "cadendar-shared";
import { type ReactNode, useEffect, useState } from "react";
import { trpc } from "../main/components/trpc.ts";

function Toggle(props: {
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <input
      type="checkbox"
      checked={props.checked}
      onChange={(event) => props.onChange(event.target.checked)}
    />
  );
}

function NumberField(props: {
  value: number;
  onChange: (value: number) => void;
  min?: number;
}) {
  return (
    <input
      className="w-20 rounded border px-2 py-1"
      type="number"
      min={props.min}
      value={props.value}
      onChange={(event) => props.onChange(Number(event.target.value))}
    />
  );
}

export function RecommendationSettingsComp(props: {
  settings: RecommendationSettings;
}) {
  const [settings, setSettingsState] = useState(props.settings);
  const [hasLocalChanges, setHasLocalChanges] = useState(false);
  const utils = trpc.useUtils();
  const setSettings = (nextSettings: RecommendationSettings) => {
    setHasLocalChanges(true);
    setSettingsState(nextSettings);
  };

  useEffect(() => {
    if (!hasLocalChanges) {
      setSettingsState(props.settings);
    }
  }, [props.settings]);

  const mutation = trpc.rule.submitRecommendationSettingsForOwner.useMutation({
    onSuccess: async () => {
      setHasLocalChanges(false);
      await utils.preference.invalidate();
      await utils.rule.getAll.invalidate();
      await utils.strategor.getPresentationSettings.invalidate();
    },
  });

  return (
    <section className="rounded border p-3">
      <h2 className="text-xl">Recommandations automatiques</h2>
      <p className="mb-3 text-sm text-slate-600">
        Ces réglages déterminent les créneaux proposés. Les valeurs initiales
        reproduisent le comportement actuel.
      </p>

      <fieldset
        className="grid gap-3 md:grid-cols-2"
        disabled={mutation.isPending}
      >
        <fieldset className="rounded border p-2">
          <legend className="px-1 font-medium">Score des créneaux</legend>
          <ScoreSetting
            label="Créneau qui remplit exactement une plage"
            setting={settings.scoring.exactFit}
            onChange={(exactFit) =>
              setSettings({
                ...settings,
                scoring: { ...settings.scoring, exactFit },
              })
            }
          />
          <ScoreSetting
            label="Début d'une plage disponible"
            setting={settings.scoring.beginningOfFreePeriod}
            onChange={(beginningOfFreePeriod) =>
              setSettings({
                ...settings,
                scoring: { ...settings.scoring, beginningOfFreePeriod },
              })
            }
          />
          <ScoreSetting
            label="Petit espace résiduel"
            setting={settings.scoring.smallResidualGap}
            onChange={(smallResidualGap) =>
              setSettings({
                ...settings,
                scoring: { ...settings.scoring, smallResidualGap },
              })
            }
          />
          <ScoreSetting
            label="Créneau adjacent à un RDV"
            setting={settings.scoring.adjacentAppointment}
            onChange={(adjacentAppointment) =>
              setSettings({
                ...settings,
                scoring: { ...settings.scoring, adjacentAppointment },
              })
            }
          />
          <label className="flex items-center justify-between gap-2 py-1">
            <span className="flex items-center gap-2">
              <Toggle
                checked={settings.scoring.regularResidualGaps.enabled}
                onChange={(enabled) =>
                  setSettings({
                    ...settings,
                    scoring: {
                      ...settings.scoring,
                      regularResidualGaps: {
                        ...settings.scoring.regularResidualGaps,
                        enabled,
                      },
                    },
                  })
                }
              />
              Espaces résiduels réguliers
            </span>
            <NumberField
              min={1}
              value={settings.scoring.regularResidualGaps.multipleOfMinutes}
              onChange={(multipleOfMinutes) =>
                setSettings({
                  ...settings,
                  scoring: {
                    ...settings.scoring,
                    regularResidualGaps: {
                      ...settings.scoring.regularResidualGaps,
                      multipleOfMinutes,
                    },
                  },
                })
              }
            />
            <NumberField
              value={settings.scoring.regularResidualGaps.score}
              onChange={(score) =>
                setSettings({
                  ...settings,
                  scoring: {
                    ...settings.scoring,
                    regularResidualGaps: {
                      ...settings.scoring.regularResidualGaps,
                      score,
                    },
                  },
                })
              }
            />
          </label>
          <PreferredEndTimesSetting
            settings={settings}
            onChange={(preferredEndTimes) =>
              setSettings({
                ...settings,
                scoring: { ...settings.scoring, preferredEndTimes },
              })
            }
          />
        </fieldset>

        <fieldset className="rounded border p-2">
          <legend className="px-1 font-medium">Durées</legend>
          <label className="flex items-center justify-between gap-2 py-1">
            Durée Cadendar par défaut (minutes)
            <NumberField
              min={1}
              value={settings.durationProfiles.cadendar.defaultDuration}
              onChange={(defaultDuration) =>
                setSettings({
                  ...settings,
                  durationProfiles: {
                    ...settings.durationProfiles,
                    cadendar: {
                      ...settings.durationProfiles.cadendar,
                      defaultDuration,
                    },
                  },
                })
              }
            />
          </label>
          <label className="flex items-center justify-between gap-2 py-1">
            Nouveau patient Cadenweb majeur (minutes)
            <NumberField
              min={1}
              value={
                settings.durationProfiles.cadenweb.newPatientAdult.duration
              }
              onChange={(duration) =>
                setSettings({
                  ...settings,
                  durationProfiles: {
                    ...settings.durationProfiles,
                    cadenweb: {
                      ...settings.durationProfiles.cadenweb,
                      newPatientAdult: {
                        ...settings.durationProfiles.cadenweb.newPatientAdult,
                        duration,
                      },
                    },
                  },
                })
              }
            />
          </label>
          <label className="flex items-center justify-between gap-2 py-1">
            <span>Utiliser la durée minimale par patient pour les familles</span>
            <Toggle
              checked={settings.durationProfiles.cadendar.multiplePatients.enabled}
              onChange={(enabled) =>
                setSettings({
                  ...settings,
                  durationProfiles: {
                    ...settings.durationProfiles,
                    cadendar: {
                      ...settings.durationProfiles.cadendar,
                      multiplePatients: { enabled },
                    },
                  },
                })
              }
            />
          </label>
          <DurationRule
            label="Patient mineur Cadendar"
            enabled={settings.durationProfiles.cadendar.minor.enabled}
            onEnabledChange={(enabled) =>
              setSettings({
                ...settings,
                durationProfiles: {
                  ...settings.durationProfiles,
                  cadendar: {
                    ...settings.durationProfiles.cadendar,
                    minor: {
                      ...settings.durationProfiles.cadendar.minor,
                      enabled,
                    },
                  },
                },
              })
            }
            fields={
              <NumberField
                min={0}
                value={settings.durationProfiles.cadendar.minor.maxAge}
                onChange={(maxAge) =>
                  setSettings({
                    ...settings,
                    durationProfiles: {
                      ...settings.durationProfiles,
                      cadendar: {
                        ...settings.durationProfiles.cadendar,
                        minor: {
                          ...settings.durationProfiles.cadendar.minor,
                          maxAge,
                        },
                      },
                    },
                  })
                }
              />
            }
          />
          <DurationRule
            label="Motifs courts, patient connu"
            enabled={
              settings.durationProfiles.cadendar.existingPatientShortMotifs
                .enabled
            }
            onEnabledChange={(enabled) =>
              setSettings({
                ...settings,
                durationProfiles: {
                  ...settings.durationProfiles,
                  cadendar: {
                    ...settings.durationProfiles.cadendar,
                    existingPatientShortMotifs: {
                      ...settings.durationProfiles.cadendar
                        .existingPatientShortMotifs,
                      enabled,
                    },
                  },
                },
              })
            }
            fields={
              <MotifListField
                value={
                  settings.durationProfiles.cadendar.existingPatientShortMotifs
                    .motifs
                }
                onChange={(motifs) =>
                  setSettings({
                    ...settings,
                    durationProfiles: {
                      ...settings.durationProfiles,
                      cadendar: {
                        ...settings.durationProfiles.cadendar,
                        existingPatientShortMotifs: {
                          ...settings.durationProfiles.cadendar
                            .existingPatientShortMotifs,
                          motifs,
                        },
                      },
                    },
                  })
                }
              />
            }
          />
          <DurationRule
            label="Nouveau patient Cadenweb majeur"
            enabled={settings.durationProfiles.cadenweb.newPatientAdult.enabled}
            onEnabledChange={(enabled) =>
              setSettings({
                ...settings,
                durationProfiles: {
                  ...settings.durationProfiles,
                  cadenweb: {
                    ...settings.durationProfiles.cadenweb,
                    newPatientAdult: {
                      ...settings.durationProfiles.cadenweb.newPatientAdult,
                      enabled,
                    },
                  },
                },
              })
            }
          />
          <CadenwebDurationRules settings={settings} onChange={setSettings} />
        </fieldset>

        <fieldset className="rounded border p-2">
          <legend className="px-1 font-medium">Report et départage</legend>
          <label className="flex items-center justify-between gap-2 py-1">
            <span>Appliquer le report tardif</span>
            <Toggle
              checked={settings.postponement.enabled}
              onChange={(enabled) =>
                setSettings({
                  ...settings,
                  postponement: { ...settings.postponement, enabled },
                })
              }
            />
          </label>
          <label className="flex items-center justify-between gap-2 py-1">
            Seuil de report (heures)
            <NumberField
              min={1}
              value={settings.postponement.thresholdHours}
              onChange={(thresholdHours) =>
                setSettings({
                  ...settings,
                  postponement: { ...settings.postponement, thresholdHours },
                })
              }
            />
          </label>
          <label className="flex items-center justify-between gap-2 py-1">
            Report de rendez-vous (semaines)
            <NumberField
              min={0}
              value={settings.postponement.delayWeeks}
              onChange={(delayWeeks) =>
                setSettings({
                  ...settings,
                  postponement: { ...settings.postponement, delayWeeks },
                })
              }
            />
          </label>
          <label className="flex items-center justify-between gap-2 py-1">
            <span>Départage aléatoire des scores égaux</span>
            <Toggle
              checked={settings.tieBreaking.enabled}
              onChange={(enabled) =>
                setSettings({
                  ...settings,
                  tieBreaking: { ...settings.tieBreaking, enabled },
                })
              }
            />
          </label>
          <label className="flex items-center justify-between gap-2 py-1">
            Renouveler le départage (heures)
            <NumberField
              min={1}
              value={settings.tieBreaking.refreshHours}
              onChange={(refreshHours) =>
                setSettings({
                  ...settings,
                  tieBreaking: { ...settings.tieBreaking, refreshHours },
                })
              }
            />
          </label>
        </fieldset>

        <fieldset className="rounded border p-2">
          <legend className="px-1 font-medium">Affichage</legend>
          <label className="flex items-center justify-between gap-2 py-1">
            Score minimal dans Cadendar
            <NumberField
              value={settings.presentation.cadendar.minimumScore}
              onChange={(minimumScore) =>
                setSettings({
                  ...settings,
                  presentation: {
                    ...settings.presentation,
                    cadendar: {
                      ...settings.presentation.cadendar,
                      minimumScore,
                    },
                  },
                })
              }
            />
          </label>
          <label className="flex items-center justify-between gap-2 py-1">
            <span>Filtrer les scores faibles dans Cadendar</span>
            <Toggle
              checked={settings.presentation.cadendar.filterByMinimumScore}
              onChange={(filterByMinimumScore) =>
                setSettings({
                  ...settings,
                  presentation: {
                    ...settings.presentation,
                    cadendar: {
                      ...settings.presentation.cadendar,
                      filterByMinimumScore,
                    },
                  },
                })
              }
            />
          </label>
          <label className="flex items-center justify-between gap-2 py-1">
            Propositions Cadenweb par jour
            <NumberField
              min={0}
              value={settings.presentation.cadenweb.candidatesPerDay}
              onChange={(candidatesPerDay) =>
                setSettings({
                  ...settings,
                  presentation: {
                    ...settings.presentation,
                    cadenweb: {
                      ...settings.presentation.cadenweb,
                      candidatesPerDay,
                    },
                  },
                })
              }
            />
          </label>
          <label className="flex items-center justify-between gap-2 py-1">
            Propositions Cadenweb par jour le soir
            <NumberField
              min={0}
              value={settings.presentation.cadenweb.candidatesPerDayWhenEvening}
              onChange={(candidatesPerDayWhenEvening) =>
                setSettings({
                  ...settings,
                  presentation: {
                    ...settings.presentation,
                    cadenweb: {
                      ...settings.presentation.cadenweb,
                      candidatesPerDayWhenEvening,
                    },
                  },
                })
              }
            />
          </label>
          <label className="flex items-center justify-between gap-2 py-1">
            Recherche automatique Cadenweb (semaines)
            <NumberField
              min={0}
              value={settings.presentation.cadenweb.automaticSearchWeeks}
              onChange={(automaticSearchWeeks) =>
                setSettings({
                  ...settings,
                  presentation: {
                    ...settings.presentation,
                    cadenweb: {
                      ...settings.presentation.cadenweb,
                      automaticSearchWeeks,
                    },
                  },
                })
              }
            />
          </label>
          <label className="flex items-center justify-between gap-2 py-1">
            <span>Sélectionner automatiquement l'unique proposition</span>
            <Toggle
              checked={settings.presentation.cadenweb.selectOnlyCandidate}
              onChange={(selectOnlyCandidate) =>
                setSettings({
                  ...settings,
                  presentation: {
                    ...settings.presentation,
                    cadenweb: {
                      ...settings.presentation.cadenweb,
                      selectOnlyCandidate,
                    },
                  },
                })
              }
            />
          </label>
        </fieldset>
      </fieldset>

      {mutation.error ? (
        <p className="mt-2 text-sm text-red-700">
          Impossible d'enregistrer les réglages : {mutation.error.message}
        </p>
      ) : null}
      <button
        className="mt-3 rounded bg-primary px-3 py-2 text-primary-foreground disabled:opacity-50"
        disabled={mutation.isPending}
        onClick={() => mutation.mutate(settings)}
      >
        {mutation.isPending ? "Enregistrement..." : "Enregistrer les réglages"}
      </button>
    </section>
  );
}

function ScoreSetting(props: {
  label: string;
  setting: { enabled: boolean; score: number };
  onChange: (setting: { enabled: boolean; score: number }) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-2 py-1">
      <span className="flex items-center gap-2">
        <Toggle
          checked={props.setting.enabled}
          onChange={(enabled) => props.onChange({ ...props.setting, enabled })}
        />
        {props.label}
      </span>
      <NumberField
        value={props.setting.score}
        onChange={(score) => props.onChange({ ...props.setting, score })}
      />
    </label>
  );
}

function PreferredEndTimesSetting(props: {
  settings: RecommendationSettings;
  onChange: (
    preferredEndTimes: RecommendationSettings["scoring"]["preferredEndTimes"],
  ) => void;
}) {
  return (
    <div className="space-y-1 py-1">
      <span className="text-sm">Fins de rendez-vous préférées</span>
      {props.settings.scoring.preferredEndTimes.map((setting, index) => (
        <div className="flex items-center gap-2" key={index}>
          <Toggle
            checked={setting.enabled}
            onChange={(enabled) =>
              props.onChange(
                props.settings.scoring.preferredEndTimes.map((item, itemIndex) =>
                  itemIndex === index ? { ...item, enabled } : item,
                ),
              )
            }
          />
          <NumberField
            min={0}
            value={setting.endMinute}
            onChange={(endMinute) =>
              props.onChange(
                props.settings.scoring.preferredEndTimes.map((item, itemIndex) =>
                  itemIndex === index ? { ...item, endMinute } : item,
                ),
              )
            }
          />
          <NumberField
            value={setting.score}
            onChange={(score) =>
              props.onChange(
                props.settings.scoring.preferredEndTimes.map((item, itemIndex) =>
                  itemIndex === index ? { ...item, score } : item,
                ),
              )
            }
          />
          <button
            className="rounded border px-2 py-1 text-sm"
            type="button"
            onClick={() =>
              props.onChange(
                props.settings.scoring.preferredEndTimes.filter(
                  (_, itemIndex) => itemIndex !== index,
                ),
              )
            }
          >
            Retirer
          </button>
        </div>
      ))}
      <button
        className="rounded border px-2 py-1 text-sm"
        type="button"
        onClick={() =>
          props.onChange([
            ...props.settings.scoring.preferredEndTimes,
            { enabled: true, endMinute: 17 * 60, score: 0 },
          ])
        }
      >
        Ajouter une heure de fin
      </button>
    </div>
  );
}

function DurationRule(props: {
  label: string;
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  fields?: ReactNode;
}) {
  return (
    <label className="flex items-center justify-between gap-2 py-1">
      <span className="flex items-center gap-2">
        <Toggle checked={props.enabled} onChange={props.onEnabledChange} />
        {props.label}
      </span>
      {props.fields}
    </label>
  );
}

function MotifListField(props: {
  value: number[];
  onChange: (motifs: number[]) => void;
}) {
  const [text, setText] = useState(props.value.join(", "));
  const [error, setError] = useState<string | null>(null);

  return (
    <span>
      <input
        aria-label="Codes motifs séparés par des virgules"
        className="w-32 rounded border px-2 py-1"
        value={text}
        onChange={(event) => {
          const value = event.target.value;
          setText(value);
          const parts = value
            .split(",")
            .map((part) => part.trim())
            .filter((part) => part.length > 0);
          const motifs = parts.map(Number);
          if (motifs.every(Number.isInteger)) {
            setError(null);
            props.onChange(motifs);
          } else {
            setError("Utilisez des codes motifs entiers séparés par des virgules.");
          }
        }}
      />
      {error ? <span className="block text-xs text-red-700">{error}</span> : null}
    </span>
  );
}

function CadenwebDurationRules(props: {
  settings: RecommendationSettings;
  onChange: (settings: RecommendationSettings) => void;
}) {
  const cadenweb = props.settings.durationProfiles.cadenweb;

  return (
    <>
      <DurationRule
        label="Nouveau patient Cadenweb mineur"
        enabled={cadenweb.newPatientMinor.enabled}
        onEnabledChange={(enabled) =>
          props.onChange({
            ...props.settings,
            durationProfiles: {
              ...props.settings.durationProfiles,
              cadenweb: {
                ...cadenweb,
                newPatientMinor: { ...cadenweb.newPatientMinor, enabled },
              },
            },
          })
        }
        fields={
          <span className="flex gap-1">
            <NumberField
              min={0}
              value={cadenweb.newPatientMinor.maxAge}
              onChange={(maxAge) =>
                props.onChange({
                  ...props.settings,
                  durationProfiles: {
                    ...props.settings.durationProfiles,
                    cadenweb: {
                      ...cadenweb,
                      newPatientMinor: { ...cadenweb.newPatientMinor, maxAge },
                    },
                  },
                })
              }
            />
            <NumberField
              min={1}
              value={cadenweb.newPatientMinor.duration}
              onChange={(duration) =>
                props.onChange({
                  ...props.settings,
                  durationProfiles: {
                    ...props.settings.durationProfiles,
                    cadenweb: {
                      ...cadenweb,
                      newPatientMinor: { ...cadenweb.newPatientMinor, duration },
                    },
                  },
                })
              }
            />
          </span>
        }
      />
      <DurationRule
        label="Motifs courts Cadenweb"
        enabled={cadenweb.shortMotifs.enabled}
        onEnabledChange={(enabled) =>
          props.onChange({
            ...props.settings,
            durationProfiles: {
              ...props.settings.durationProfiles,
              cadenweb: {
                ...cadenweb,
                shortMotifs: { ...cadenweb.shortMotifs, enabled },
              },
            },
          })
        }
        fields={
          <span className="flex gap-1">
            <MotifListField
              value={cadenweb.shortMotifs.motifs}
              onChange={(motifs) =>
                props.onChange({
                  ...props.settings,
                  durationProfiles: {
                    ...props.settings.durationProfiles,
                    cadenweb: {
                      ...cadenweb,
                      shortMotifs: { ...cadenweb.shortMotifs, motifs },
                    },
                  },
                })
              }
            />
            <NumberField
              min={1}
              value={cadenweb.shortMotifs.duration}
              onChange={(duration) =>
                props.onChange({
                  ...props.settings,
                  durationProfiles: {
                    ...props.settings.durationProfiles,
                    cadenweb: {
                      ...cadenweb,
                      shortMotifs: { ...cadenweb.shortMotifs, duration },
                    },
                  },
                })
              }
            />
          </span>
        }
      />
      <DurationRule
        label="Dent cassée Cadenweb"
        enabled={cadenweb.brokenTooth.enabled}
        onEnabledChange={(enabled) =>
          props.onChange({
            ...props.settings,
            durationProfiles: {
              ...props.settings.durationProfiles,
              cadenweb: {
                ...cadenweb,
                brokenTooth: { ...cadenweb.brokenTooth, enabled },
              },
            },
          })
        }
        fields={
          <span className="flex gap-1">
            <NumberField
              value={cadenweb.brokenTooth.motif}
              onChange={(motif) =>
                props.onChange({
                  ...props.settings,
                  durationProfiles: {
                    ...props.settings.durationProfiles,
                    cadenweb: {
                      ...cadenweb,
                      brokenTooth: { ...cadenweb.brokenTooth, motif },
                    },
                  },
                })
              }
            />
            <NumberField
              min={1}
              value={cadenweb.brokenTooth.duration}
              onChange={(duration) =>
                props.onChange({
                  ...props.settings,
                  durationProfiles: {
                    ...props.settings.durationProfiles,
                    cadenweb: {
                      ...cadenweb,
                      brokenTooth: { ...cadenweb.brokenTooth, duration },
                    },
                  },
                })
              }
            />
          </span>
        }
      />
    </>
  );
}
